import type * as React from "react";
import type { Option } from "./types";

type SharedOptionArgs = {
  options: Option[];
  selectedValue: unknown;
  multi: boolean;
  searchQuery: string;
  optionsCache: React.MutableRefObject<Record<string, Option>>;
};

type OpenSessionSelectionArgs = {
  isOpen: boolean;
  multi: boolean;
  selectedValue: unknown;
  selectedValueAtOpen: unknown;
  wasOpen: boolean;
};

export function resolveComboboxOpenSessionSelection({
  isOpen,
  multi,
  selectedValue,
  selectedValueAtOpen,
  wasOpen,
}: OpenSessionSelectionArgs) {
  if (!isOpen || !multi) {
    return selectedValue;
  }

  return wasOpen ? selectedValueAtOpen : selectedValue;
}

function getSelectableValue(value: unknown): string | null {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return null;
}

function getSelectableValues(value: unknown): string[] {
  if (!Array.isArray(value)) {
    const selected = getSelectableValue(value);
    return selected ? [selected] : [];
  }

  return value.flatMap((item) => {
    const selected = getSelectableValue(item);
    return selected ? [selected] : [];
  });
}

function getFallbackComboboxLabel(value: string) {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  if (isOpaqueComboboxValue(trimmedValue)) {
    return null;
  }

  return trimmedValue
    .replace(/[_-]+/g, " ")
    .replace(/(^|\s)([a-z])/g, (_match, prefix: string, character: string) => `${prefix}${character.toUpperCase()}`);
}

function isOpaqueComboboxValue(value: string) {
  return value.includes(":")
    || /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
    || /^c[a-z0-9]{20,}$/i.test(value)
    || /^[a-z0-9]{20,}$/i.test(value);
}

export function prioritizeComboboxOptions({
  options,
  selectedValue,
  multi,
  searchQuery,
  optionsCache,
}: SharedOptionArgs) {
  if (searchQuery || !selectedValue) {
    return options;
  }

  if (multi) {
    const selected = getSelectableValues(selectedValue);
    if (selected.length === 0) {
      return options;
    }

    const optionsByValue = new Map(options.map(option => [option.value, option]));
    const selectedOptions = selected.flatMap((value) => {
      const option = optionsByValue.get(value) ?? optionsCache.current[value];
      return option ? [option] : [];
    });
    const selectedValues = new Set(selectedOptions.map(option => option.value));

    return [
      ...selectedOptions,
      ...options.filter(option => !selectedValues.has(option.value)),
    ];
  }

  const selected = getSelectableValue(selectedValue);
  if (!selected) {
    return options;
  }
  const selectedOption = optionsCache.current[selected]
    || options.find((option) => option.value === selected);

  if (!selectedOption) {
    return options;
  }

  return [selectedOption, ...options.filter((option) => option.value !== selected)];
}

export function getComboboxDisplayValue({
  selectedValue,
  multi,
  placeholder,
  options,
  optionsCache,
}: Omit<SharedOptionArgs, "searchQuery"> & {
  placeholder?: string;
}) {
  if (multi) {
    const selected = getSelectableValues(selectedValue);
    if (selected.length === 0) {
      return placeholder && placeholder !== "" ? placeholder : "Select";
    }

    const labels = selected
      .map((value) => {
        const option = optionsCache.current[value]
          || options.find((candidate) => candidate.value === value);
        return option?.label || getFallbackComboboxLabel(value);
      })
      .filter((label): label is string => Boolean(label));

    if (labels.length === 0) {
      return placeholder && placeholder !== "" ? placeholder : "Select";
    }

    return labels
      .join(", ");
  }

  const selected = getSelectableValue(selectedValue);
  if (!selected) {
    return placeholder && placeholder !== ""
      ? placeholder
      : "Select an option";
  }

  const option = optionsCache.current[selected]
    || options.find((candidate) => candidate.value === selected);

  if (!option) {
    return getFallbackComboboxLabel(selected)
      ?? (placeholder && placeholder !== "" ? placeholder : "Select an option");
  }

  return option;
}

export function filterComboboxOptions(options: Option[], searchQuery: string) {
  if (!searchQuery) {
    return options.slice(0, 500);
  }

  const search = searchQuery.toLowerCase();
  return options
    .map((option) => {
      const label = option.label.toLowerCase();
      const group = option.group?.toLowerCase() || "";

      let score = 0;

      if (label === search) {
        score = 1000;
      } else if (label.startsWith(search)) {
        score = 900;
      } else if (label.includes(search)) {
        score = 800 - label.indexOf(search);
      } else if (group.includes(search)) {
        score = 400;
      } else {
        const searchTerms = search.split(/\s+/);
        const matchCount = searchTerms.filter((term) => label.includes(term) || group.includes(term)).length;
        score = matchCount * 100;
      }

      return { option, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 500)
    .map((item) => item.option);
}

export function groupComboboxOptions(options: Option[]) {
  if (!options.some((option) => option.group)) {
    return null;
  }

  return options.reduce<Record<string, Option[]>>((acc, option) => {
    const group = option.group || "Other";
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(option);
    return acc;
  }, {});
}
